import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { confirmPaymentByPlanName, markOrderAsPaypalPending } from '../lib/db/orders';
import { createNotification } from '../lib/db/operations';

const PaymentResult = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isLoaded } = useUser();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Procesando tu pago...');

  useEffect(() => {
    if (!isLoaded) return;

    if (!user) {
      setStatus('error');
      setMessage('Debes iniciar sesión para procesar el pago.');
      return;
    }

    const plan = searchParams.get('plan');
    const paymentStatus = searchParams.get('estado_pago') || searchParams.get('status');

    if (!plan) {
      setStatus('error');
      setMessage('No se especificó el plan.');
      return;
    }

    const zohoPlans = ['Mail Zoho', '5Gb Mail Zoho'];
    const isZohoPending = zohoPlans.includes(plan) && (paymentStatus === 'pendiente' || paymentStatus === 'pending');

    if (!isZohoPending && (paymentStatus !== 'exito' && paymentStatus !== 'success')) {
      setStatus('error');
      setMessage('El pago no se completó exitosamente o fue cancelado.');
      return;
    }

    const processPayment = async () => {
      try {
        let result;
        if (isZohoPending) {
          result = await markOrderAsPaypalPending(user.id, plan);
          if (result.success && result.data) {
            // Create notification for manual review
            await createNotification(
              result.data.userId,
              'Revisión',
              'Este pago se revisa de manera manual nos comunicaremos cuando este procesado'
            );
          }
        } else {
          result = await confirmPaymentByPlanName(user.id, plan);
        }
        
        if (result.success) {
          setStatus('success');
          if (isZohoPending) {
            setMessage('Este pago se revisa de manera manual nos comunicaremos cuando este procesado');
          } else {
            setMessage('¡Pago confirmado exitosamente! Tu servicio está siendo activado.');
          }
          setTimeout(() => {
            navigate('/dashboard');
          }, 3000);
        } else {
          setStatus('error');
          setMessage(result.error || 'No se pudo confirmar el pago. Por favor contacta a soporte.');
        }
      } catch (error) {
        console.error('Payment processing error:', error);
        setStatus('error');
        setMessage('Ocurrió un error al procesar el pago.');
      }
    };

    processPayment();
  }, [isLoaded, user, searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex flex-col items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 p-8 rounded-xl shadow-lg max-w-md w-full text-center">
        {status === 'loading' && (
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Procesando Pago</h2>
            <p className="text-gray-600 dark:text-gray-400">{message}</p>
          </div>
        )}
        
        {status === 'success' && (
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              {message.includes('manual') ? 'Revisión' : '¡Pago Exitoso!'}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">{message}</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors w-full font-medium"
            >
              Ir al Dashboard
            </button>
          </div>
        )}

        {status === 'error' && (
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Error en el Pago</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">{message}</p>
            <div className="space-y-3 w-full">
              <button
                onClick={() => navigate('/dashboard')}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors w-full font-medium"
              >
                Ir al Dashboard
              </button>
              <button
                onClick={() => window.location.href = 'mailto:soporte@escalable.host'}
                className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors w-full font-medium"
              >
                Contactar Soporte
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentResult;
